import { FC, FormEvent, useState } from 'react';
import {
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
} from 'primereact/autocomplete';
import { formatDate } from '@/utils/formatDate';
import { Divider } from 'primereact/divider';
import { useTranslation } from 'react-i18next';
import Button from '../../../Button/Button';
import DropdownField from '../../../DropdownField/DropdownField';
import AutoCompleteField from '../../../AutoCompleteField/AutoCompleteField';
import { Schedule } from '../../types';
import styles from './SearchForm.module.scss';

type SearchFormProps = {
  schedule?: Schedule[];
  onSubmit: (selected: Schedule) => void;
  clearResult: () => void;
};

const SearchForm: FC<SearchFormProps> = ({
  schedule,
  onSubmit,
  clearResult,
}) => {
  const { t } = useTranslation('schedule');

  const [service, setService] = useState<string | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<{
    label: string;
    value: Schedule;
  } | null>(null);
  const [filteredShipNames, setFilteredShipNames] = useState<
    { label: string; value: Schedule }[] | undefined
  >([]);

  const uniqueServices = new Set(schedule?.map((item) => item.servicio));
  const serviceValues = Array.from(uniqueServices).map((servicio) => ({
    name: servicio,
    value: servicio,
  }));

  const mapFunction = (item: Schedule) => {
    let value = item.nombre;

    const date = new Date(item?.comienzoOperaciones);

    const dateFormatted = formatDate(date);

    value = `${value} - ${dateFormatted}`;

    return { label: value, value: item };
  };

  const shipOptions = (schedule?.map(mapFunction) || []).sort((a, b) => {
    const dateA = new Date(a.value.comienzoOperaciones);
    const dateB = new Date(b.value.comienzoOperaciones);
    return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
  });

  const searchShipName = (event: AutoCompleteCompleteEvent) => {
    const filtered = service
      ? shipOptions.filter(
          (opt) =>
            opt.value.servicio === service &&
            opt.label.toLowerCase().includes(event.query.toLowerCase())
        )
      : shipOptions.filter((opt) =>
          opt.label.toLowerCase().includes(event.query.toLowerCase())
        );
    setFilteredShipNames(filtered);
  };

  const handleServiceChange = (e: { value: string | null }) => {
    setService(e.value);
    clearResult();
  };

  const handleShipNameChange = (e: AutoCompleteChangeEvent) => {
    setSelectedSchedule(e.value);
    clearResult();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedSchedule) {
      onSubmit(selectedSchedule.value);
    }
  };

  const isSearchDisabled = !selectedSchedule;

  return (
    <form onSubmit={handleSubmit}>
      <DropdownField
        label={t('searchByShipForm.serviceLabel')}
        placeholder={t('searchByShipForm.servicePlaceholder')}
        id="service"
        value={service}
        options={serviceValues}
        onChange={handleServiceChange}
        className={styles.dropdown}
        showClear
      />
      <AutoCompleteField
        placeholder={t('searchByShipForm.shipNamePlaceholder')}
        label={t('searchByShipForm.shipNameLabel')}
        value={selectedSchedule}
        suggestions={filteredShipNames}
        completeMethod={searchShipName}
        field="label"
        id="shipName"
        onChange={handleShipNameChange}
        required
        dropdown
      />
      <Divider className={styles.divider} />
      <Button
        type="submit"
        disabled={isSearchDisabled}
        className={styles.submitButton}
      >
        {t('searchByShipForm.submitButton')}
      </Button>
    </form>
  );
};

export default SearchForm;
